/**
 * @typedef {Object} HistoryDetailFlightInfoArgs
 * @property {string | null} [airline]
 * @property {string | null} [number]
 * @property {string | null} [terminal]
 * @property {number | null} [totalPassengers]
 * @property {number | null} [totalLargeBags]
 * @property {number | null} [totalSmallBags]
 * @property {ISO_TIMESTAMP | null} [arrivalTime]
 * @property {LOCAL_DATETIME | null} [arrivalTimeLocal]
 * @property {'pickup' | 'destination' | null} [type]
 */

/**
 * Domain model describing the flight associated with a booking (e.g. an airport
 * pickup or drop-off).
 *
 * A booking may have no flight attached to it. Rather than representing that
 * absence with `null`/`undefined` and forcing every caller to null-check, this
 * model is always present on a {@link BookingHistoryDetail} and answers
 * {@link HistoryDetailFlightInfo#hasFlightInfo} so callers can ask whether any
 * flight information is actually available. When none is, an "empty" instance is
 * used in which every field is `null`.
 */
export class HistoryDetailFlightInfo {
  /**
   * Prefer the {@link HistoryDetailFlightInfo.fromApiDto} factory over calling
   * this directly — it handles the "no flight info" case for you.
   *
   * @param {HistoryDetailFlightInfoArgs} [args]
   */
  constructor(args = {}) {
    /** @type {string | null} */
    this.airline = args.airline ?? null;
    /** @type {string | null} */
    this.number = args.number ?? null;
    /** @type {string | null} */
    this.terminal = args.terminal ?? null;
    /** @type {number | null} */
    this.totalPassengers = args.totalPassengers ?? null;
    /** @type {number | null} */
    this.totalLargeBags = args.totalLargeBags ?? null;
    /** @type {number | null} */
    this.totalSmallBags = args.totalSmallBags ?? null;
    /** @type {ISO_TIMESTAMP | null} */
    this.arrivalTime = args.arrivalTime ?? null;
    /** @type {LOCAL_DATETIME | null} */
    this.arrivalTimeLocal = args.arrivalTimeLocal ?? null;
    /** @type {'pickup' | 'destination' | null} */
    this.type = args.type ?? null;
  }

  /**
   * Builds a {@link HistoryDetailFlightInfo} from the optional `flightInfo`
   * block of a booking's `bookingProperties`. When the API provides no flight
   * info (the value is `null`/`undefined`), an {@link HistoryDetailFlightInfo.empty}
   * instance is returned instead so the caller always receives a usable model.
   *
   * @param {HistoryDetailFlightInfoArgs | null} [flightInfo]
   * @returns {HistoryDetailFlightInfo}
   */
  static fromApiDto(flightInfo) {
    if (!flightInfo) {
      return HistoryDetailFlightInfo.empty();
    }

    return new HistoryDetailFlightInfo(flightInfo);
  }

  /**
   * Creates an empty instance representing a booking with no associated flight.
   * Every field is `null` and {@link HistoryDetailFlightInfo#hasFlightInfo}
   * returns `false`.
   *
   * @returns {HistoryDetailFlightInfo}
   */
  static empty() {
    return new HistoryDetailFlightInfo();
  }

  /**
   * Answers whether any flight information is available for this booking.
   *
   * @returns {boolean} `true` when at least one flight field is populated,
   *   `false` for an empty instance.
   */
  hasFlightInfo() {
    return [
      this.airline,
      this.number,
      this.terminal,
      this.totalPassengers,
      this.totalLargeBags,
      this.totalSmallBags,
      this.arrivalTime,
      this.arrivalTimeLocal,
      this.type,
    ].some((value) => value !== null && value !== undefined);
  }
}
